import { useEffect } from 'react';

interface ContentProtectionOptions {
  disablePrint?: boolean;
  disableRightClick?: boolean;
  disableTextSelection?: boolean;
  disableCopy?: boolean;
  disableScreenshot?: boolean;
}

/**
 * Custom hook to implement content protection features
 * Prevents unauthorized copying of training materials
 */
export const useContentProtection = (options: ContentProtectionOptions = {}) => {
  const {
    disablePrint = true,
    disableRightClick = true,
    disableTextSelection = true,
    disableCopy = true,
    disableScreenshot = true,
  } = options;

  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e: MouseEvent) => {
      if (disableRightClick) {
        e.preventDefault();
        return false;
      }
    };

    // Disable copy, cut, and paste
    const handleCopy = (e: ClipboardEvent) => {
      if (disableCopy) {
        e.preventDefault();
        return false;
      }
    };

    // Disable keyboard shortcuts for printing and screenshots
    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable Ctrl+P / Cmd+P (Print)
      if (disablePrint && (e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+C / Cmd+C (Copy)
      if (disableCopy && (e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+X / Cmd+X (Cut)
      if (disableCopy && (e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+S / Cmd+S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+U / Cmd+U (View Source)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        return false;
      }

      // Disable F12 (Developer Tools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+Shift+I / Cmd+Shift+I (Developer Tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+Shift+J / Cmd+Shift+J (Console)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+Shift+C / Cmd+Shift+C (Inspect Element)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        return false;
      }

      // Disable PrintScreen key
      if (disableScreenshot && (e.key === 'PrintScreen' || e.key === 'Print')) {
        e.preventDefault();
        // Alert user that screenshots are disabled
        alert('Screenshots are disabled for this content.');
        return false;
      }
    };

    // Disable drag and drop
    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    if (disableRightClick) {
      document.addEventListener('contextmenu', handleContextMenu);
    }
    if (disableCopy) {
      document.addEventListener('copy', handleCopy);
      document.addEventListener('cut', handleCopy);
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, [disablePrint, disableRightClick, disableTextSelection, disableCopy, disableScreenshot]);

  // Return CSS styles for content protection
  const protectionStyles: React.CSSProperties = {
    userSelect: disableTextSelection ? 'none' : 'auto',
    WebkitUserSelect: disableTextSelection ? 'none' : 'auto',
    MozUserSelect: disableTextSelection ? 'none' : 'auto',
  } as React.CSSProperties;

  // Return CSS string for print protection
  const printProtectionCSS = disablePrint
    ? `
      @media print {
        body {
          display: none !important;
        }
      }
      @page {
        size: 0;
        margin: 0;
      }
    `
    : '';

  return {
    protectionStyles,
    printProtectionCSS,
  };
};
